// Reviews → "Connect your software" screen.
//
// This is the customer-facing last mile of the review-solicitation engine:
// the contractor connects their job/field software (or Bloom's Zapier app) so
// completed-job customers flow into Bloom, which then sends a single review
// request per customer. Connecting REQUIRES accepting the consent attestation;
// the routing token + signing secret are minted only on accept and shown ONCE.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  crm,
  type CrmConnectionSummary,
  type CrmConnectResult,
  type CrmProvider,
} from '../lib/api.js';
import { Card, PageHeader, SectionLabel, StatusPill } from '../components/ui.js';

/**
 * The exact consent text the contractor accepts. MIRRORS the server constant
 * CRM_CONSENT_ATTESTATION_TEXT (src/api/routes/dashboardCrm.ts) — the server
 * persists its own copy as the durable record, so if you change one, change
 * both. 🔴 PLACEHOLDER pending legal sign-off before the first live connection.
 */
const CONSENT_ATTESTATION_TEXT =
  'I confirm that the customers whose contact details flow into Bloom through ' +
  'this connection gave my business their information in the normal course of ' +
  'service, and that I am authorized to ask them for a review on my behalf. ' +
  'Bloom will email a single review request per customer and honor every ' +
  'opt-out. I can disconnect at any time to stop all future requests.';

const PROVIDER_LABELS: Record<CrmProvider, string> = {
  zapier: 'Zapier (connect 6,000+ apps)',
  generic_webhook: 'Generic webhook (advanced / custom)',
};

export function ReviewsPage() {
  const qc = useQueryClient();
  const status = useQuery<{ connections: CrmConnectionSummary[] }, ApiError>({
    queryKey: ['crm', 'status'],
    queryFn: crm.status,
    staleTime: 15_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['crm', 'status'] });

  if (status.isLoading) {
    return (
      <div className="space-y-12">
        <PageHeader eyebrow="Reviews" title="Get more reviews" subtitle="Loading…" />
      </div>
    );
  }
  if (status.isError || !status.data) {
    return (
      <div className="space-y-12">
        <PageHeader
          eyebrow="Reviews"
          title="Get more reviews"
          subtitle={`Failed to load: ${status.error?.message ?? 'unknown'}`}
        />
      </div>
    );
  }

  const connections = status.data.connections;
  const active = connections.filter((c) => c.status === 'connected');

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Reviews"
        title="Get more reviews"
        subtitle="Connect your job software so Bloom asks each happy customer for a review automatically — one polite request per customer, every opt-out honored."
      />

      {active.length > 0 && (
        <section className="space-y-5">
          <SectionLabel>Connected software</SectionLabel>
          <div className="space-y-4">
            {active.map((c) => (
              <ConnectionCard key={c.id} conn={c} onChanged={invalidate} />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-5">
        <SectionLabel>{active.length > 0 ? 'Connect another' : 'Connect your software'}</SectionLabel>
        <ConnectCard onConnected={invalidate} />
      </section>
    </div>
  );
}

// ── A live connection row + disconnect ──────────────────────────────

function ConnectionCard({ conn, onChanged }: { conn: CrmConnectionSummary; onChanged: () => void }) {
  const [suppressed, setSuppressed] = useState<number | null>(null);
  const disconnect = useMutation({
    mutationFn: () => crm.disconnect(conn.id),
    onSuccess: (r) => {
      setSuppressed(r.suppressed);
      onChanged();
    },
  });
  return (
    <Card>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl text-cream">{conn.external_ref ?? conn.provider}</span>
            <StatusPill tone="ok">connected</StatusPill>
          </div>
          <p className="text-sm text-cream/55">
            {PROVIDER_LABELS[conn.provider as CrmProvider] ?? conn.provider}
            {conn.consent_attested_at && ` · consent recorded ${formatDate(conn.consent_attested_at)}`}
          </p>
          {suppressed !== null && (
            <p className="text-xs text-amber-100/80">
              Disconnected. {suppressed} not-yet-contacted customer{suppressed === 1 ? '' : 's'} suppressed — they
              will not receive a request.
            </p>
          )}
        </div>
        <SecondaryButton onClick={() => disconnect.mutate()} busy={disconnect.isPending}>
          Disconnect
        </SecondaryButton>
      </div>
    </Card>
  );
}

// ── The connect form + one-time secret reveal ───────────────────────

function ConnectCard({ onConnected }: { onConnected: () => void }) {
  const [provider, setProvider] = useState<CrmProvider>('zapier');
  const [label, setLabel] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [minted, setMinted] = useState<CrmConnectResult | null>(null);

  const connect = useMutation({
    mutationFn: () => crm.connect(provider, label.trim()),
    onSuccess: (r) => {
      setMinted(r);
      onConnected();
      // Reset the form so a second connect starts clean, but KEEP `minted`
      // on-screen until the user dismisses it (the secret shows only once).
      setLabel('');
      setAccepted(false);
    },
  });

  const canSubmit = label.trim().length > 0 && accepted && !connect.isPending;

  if (minted) {
    return <SecretReveal result={minted} onDone={() => setMinted(null)} />;
  }

  return (
    <Card>
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) connect.mutate();
        }}
      >
        <div className="space-y-2">
          <span className="block text-[10px] uppercase tracking-[0.22em] text-cream/40">Your software</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {(Object.keys(PROVIDER_LABELS) as CrmProvider[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setProvider(p)}
                className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
                  provider === p
                    ? 'border-cream/30 bg-cream/[0.08] text-cream'
                    : 'border-cream/[0.12] bg-black/30 text-cream/70 hover:border-cream/20'
                }`}
              >
                {PROVIDER_LABELS[p]}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-1">
            Name this connection
          </span>
          <input
            type="text"
            placeholder="e.g. Orlando Spray Foam — Jobber"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full rounded-lg border border-cream/[0.12] bg-black/40 px-3 py-2 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-cream/30"
          />
        </label>

        <label className="flex items-start gap-3 rounded-xl border border-cream/[0.10] bg-cream/[0.015] p-4 cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 accent-cream"
          />
          <span className="text-sm text-cream/75 leading-relaxed">{CONSENT_ATTESTATION_TEXT}</span>
        </label>

        <div className="flex items-center justify-end">
          <PrimaryButton type="submit" busy={connect.isPending} error={connect.error} disabled={!canSubmit}>
            Connect
          </PrimaryButton>
        </div>
      </form>
    </Card>
  );
}

function SecretReveal({ result, onDone }: { result: CrmConnectResult; onDone: () => void }) {
  return (
    <Card>
      <div className="space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-serif text-2xl text-cream">You're connected</span>
          <StatusPill tone="ok">connected</StatusPill>
        </div>
        <div className="rounded-xl border border-amber-300/[0.22] bg-amber-500/[0.05] p-4 text-sm text-amber-100/90">
          Copy your signing secret now — for your security it is shown only this once and cannot be retrieved later.
          You'll paste both values into {result.provider === 'zapier' ? 'Zapier' : 'your webhook setup'}.
        </div>

        <CopyField label="Webhook URL" value={result.webhook_url} />
        <CopyField label="Signing secret" value={result.signing_secret} mono />

        <div className="flex items-center justify-end">
          <PrimaryButton onClick={onDone}>I've copied both — done</PrimaryButton>
        </div>
      </div>
    </Card>
  );
}

// ── primitives (local, matching Integrations.tsx) ───────────────────

function CopyField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-xl border border-cream/[0.08] bg-cream/[0.015] p-4 space-y-2">
      <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">{label}</div>
      <div className="flex items-center gap-2 flex-wrap">
        <code className={`text-xs text-cream/85 break-all ${mono ? 'font-mono' : ''}`}>{value}</code>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-cream/[0.06] text-cream/70 hover:bg-cream/[0.12] border border-cream/[0.10] transition-colors"
    >
      {copied ? 'copied' : 'copy'}
    </button>
  );
}

function PrimaryButton({
  children,
  onClick,
  busy,
  error,
  type = 'button',
  disabled = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  busy?: boolean;
  error?: ApiError | Error | null;
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type={type}
        onClick={onClick}
        disabled={busy || disabled}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full bg-cream text-black hover:bg-cream/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? 'Working…' : children}
      </button>
      {error && <span className="text-[11px] text-rose-300/80 font-mono">{error.message}</span>}
    </div>
  );
}

function SecondaryButton({
  children,
  onClick,
  busy,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full bg-cream/[0.06] text-cream/85 hover:bg-cream/[0.12] border border-cream/[0.14] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}
